import { Component, computed, inject, OnInit, signal, viewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ServicePortalApi } from '../../../api/api';
import { TestUser, UserCredentials } from '../../../api/types';
import {
  SynBreadcrumbComponent,
  SynBreadcrumbItemComponent,
  SynButtonComponent, SynCheckboxComponent,
  SynDialogComponent,
  SynIconComponent, SynSpinnerComponent,
} from '@synergy-design-system/angular';
import { PasswordInputComponent } from '../../components/password-input.component';
import { TestUserSettingsComponent } from './test-user-settings.component';
import { StateService } from '../../../api/state';

@Component({
  selector: 'app-edit-customer-account',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/test-users" i18n="@@test-users">Test Users</syn-breadcrumb-item>
      <syn-breadcrumb-item>{{ user()?.username ?? '' }}</syn-breadcrumb-item>
    </syn-breadcrumb>

    <div class="flex-row gap-2 align-center margin-bottom-1">
      <h1 i18n="@@edit-test-user">Edit Test User</h1>
      <syn-button
        (click)="openResetPasswordDialog()"
        size="small"
      >
        <syn-icon name="lock_fill" slot="prefix" />
        <span i18n="@@reset-password">Reset Password</span>
      </syn-button>

      <syn-button
        (click)="openDeleteDialog()"
        size="small"
        data-e2e="btn-delete-test-user"
      >
        <syn-icon name="delete_fill" slot="prefix" />
        <span i18n="@@delete-test-user">Delete Test User</span>
      </syn-button>
    </div>

    @if (user()) {
      <app-test-user-settings
        [user]="user()!"
      />
    } @else {
      <syn-spinner />
    }

    <syn-dialog
      label="Delete Test User"
      i18n-label="@@delete-test-user"
      #deleteDialog>
      <div>
        <p i18n="@@delete-test-user-warning">
          Do you want to <strong>permanently delete</strong> the following test user?
        </p>
        <span>{{ this.displayName() }}</span>
      </div>
      <div slot="footer" class="flex-row gap-1 justify-end">
        <syn-button i18n="@@cancel" (click)="closeDeleteDialog()">Cancel</syn-button>
        <syn-button variant="filled" (click)="onDeleteTestUser()" [disabled]="requestRunning()"
                    data-e2e="btn-confirm-delete-test-user">
          <span i18n="@@delete-test-user">Delete Test User</span>
        </syn-button>
      </div>
    </syn-dialog>

    <syn-dialog
      label="Reset Password"
      i18n-label="@@reset-password"
      #resetPasswordDialog>
      <div class="flex-col gap-2">
        <app-password-input
          label="Password" i18n-label="@@password"
          [(password)]="password"
          [alwaysShowRequirements]="true"
        />
        <syn-checkbox
          [(checked)]="temporary"
        >
          <span i18n="@@temporary">Temporary</span>
        </syn-checkbox>
      </div>
      <div slot="footer" class="flex-row gap-1 justify-end">
        <syn-button i18n="@@cancel" (click)="closeResetPasswordDialog()">Cancel</syn-button>
        <syn-button variant="filled" (click)="onResetPassword()" [disabled]="requestRunning()">
          <span i18n="@@reset-password">Reset Password</span>
        </syn-button>
      </div>
    </syn-dialog>
  `,
  styles: [],
  imports: [
    SynButtonComponent,
    SynIconComponent,
    SynDialogComponent,
    RouterLink,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    SynCheckboxComponent,
    PasswordInputComponent,
    TestUserSettingsComponent,
    SynSpinnerComponent,
  ],
})
export class EditTestUserComponent implements OnInit {
  userId = inject(ActivatedRoute).snapshot.params['userId'];
  api = inject(ServicePortalApi);
  state = inject(StateService);
  router = inject(Router);

  user: WritableSignal<TestUser | null> = signal(null);
  displayName = computed(() => {
    const user = this.user();
    if (user == null) return '(unknown)';

    const firstName = user.firstName ?? '';
    const lastName = user.lastName ?? '';
    return firstName + ' ' + lastName + ' (' + user.username + ')';
  });

  password = signal('');
  temporary = signal(true);
  requestRunning = signal(false);

  deleteDialog = viewChild.required<SynDialogComponent>('deleteDialog');
  resetPasswordDialog = viewChild.required<SynDialogComponent>('resetPasswordDialog');

  ngOnInit() {
    const user = this.state.lookupTestUser(this.userId);
    if (user) {
      this.user.set(user);
    } else {
      this.api.getTestUser(this.userId, (value) => {
        this.user.set(value);
      });
    }
  }

  openDeleteDialog() {
    void this.deleteDialog().nativeElement?.show();

  }

  closeDeleteDialog() {
    void this.deleteDialog().nativeElement?.hide();
  }

  onDeleteTestUser() {
    this.requestRunning.set(true);
    this.api.deleteTestUser(this.userId, () => {
      console.log('Test user deleted', this.userId);
      this.requestRunning.set(false);
      void this.router.navigate(['test-users']);
    });
  }

  openResetPasswordDialog() {
    this.password.set('');
    this.temporary.set(true);

    void this.resetPasswordDialog().nativeElement.show();
  }

  closeResetPasswordDialog() {
    void this.resetPasswordDialog().nativeElement.hide();
  }

  onResetPassword() {
    const credentials: UserCredentials = {
      password: this.password(),
      temporary: this.temporary(),
    };

    this.requestRunning.set(true);
    this.api.updateTestUserCredentials(this.userId, credentials, () => {
      console.log('Updated credentials');
      this.closeResetPasswordDialog();
      this.requestRunning.set(false);
    });
  }
}
